/**
 * `useAnalyticsView` — the Analytics page's saved-view state, in the exact shape the
 * shared `ViewsDropdown` already consumes.
 *
 * This hook is the whole of the "roles become views" change on the state side. It
 * deliberately owns NO persistence of its own: `ViewsDropdown` + `useTableViews` +
 * user preferences already store an opaque `TableViewState` per `viewKey`, and this
 * hook just supplies the analytics-shaped payload and applies one back. Building a
 * second views system was the thing to avoid.
 *
 * ## What lives where, and why
 *
 * | State | Owner | Persisted in a view? |
 * |---|---|---|
 * | Which cards are on the canvas | this hook (local) | yes |
 * | Where each card sits, per breakpoint | this hook (local) | yes |
 * | Dimension filters + each panel's data type | redux `state.analytics.filters` | yes |
 * | Date range | redux, written ONLY by the page date picker | **no** (see `AnalyticsDimensionFilters`) |
 * | Which card is expanded | the page (local) | **no** — ephemeral |
 *
 * Filters are read from and written to redux rather than mirrored here, because the
 * filter bar is their existing writer and a second copy would drift the moment the
 * drawer changed anything. Applying a view dispatches; the current state reads back.
 *
 * ## Layouts are NORMALISED on the way in and on the way out
 *
 * Every layout this hook holds has been through `toPersistedItems`, which strips it to
 * the five persisted fields and sorts by card id. Both halves matter. Stripping keeps
 * a card's CONSTRAINTS out of the saved state, so tightening the map's minimum size
 * later reaches views that were saved before it changed. Sorting makes the state
 * order-independent, so dragging a card away and back produces a byte-identical
 * payload — and `ViewsDropdown` compares by `stableStringify`, which would otherwise
 * report a view as modified when nothing about it had changed.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useDispatch, useSelector } from 'store/hooks'
import { setAnalyticsFilters } from 'store/analytics/actions'
import { useTableViews } from 'hooks'
import type {
  AnalyticsGridBreakpoint,
  AnalyticsGridItem,
  AnalyticsGridLayouts,
  AnalyticsViewState,
  TableViewState,
} from 'types'

import { decodeRing, encodeRing } from 'utils/geoRing'

import {
  ANALYTICS_SEEDED_VIEWS,
  DEFAULT_ANALYTICS_VIEW_ID,
  DEFAULT_ANALYTICS_VIEW_STATE,
} from './analyticsViewSeeds'
import {
  analyticsVariantCardId,
  composeAnalyticsVariantCardId,
  parseAnalyticsVariantCardId,
  regroupAnalyticsVariantCardId,
  removeAnalyticsVariantSeries,
  toPersistedItems,
} from './analyticsGrid'
import {
  migrateAnalyticsCardId,
  migrateAnalyticsCardIds,
  type AnalyticsMapGrouping,
} from './analyticsDisplay'
// A card's bucket width is keyed by its id, so an id rewrite has to carry it — see
// `renameCardId` below for what goes wrong when it does not (VRG-512).
import { renameAnalyticsCardGranularity } from './analyticsCardGranularity'

export const ANALYTICS_VIEW_KEY = 'analytics'

/**
 * ── VRG-481: EVERY VIEW IS FORWARD-MAPPED ON THE WAY IN ─────────────────────────────
 *
 * `migratedState` is the ONE door a stored `AnalyticsViewState` comes through. Both
 * halves of the persisted state carry card ids and both are mapped:
 *
 *   `cards`               the membership + order
 *   `layouts[bp][].i`     the geometry, keyed by the same id
 *
 * ⚠️ Mapping only `cards` is the half-fix, and it is silent: the card would survive the
 * migration and its ARRANGEMENT would not — `resolveLayout` looks the new id up in the
 * saved layout, misses, and packs the card at the bottom of the dashboard. A view that
 * opens with the right cards in the wrong places reads as "the layout reset itself",
 * which is exactly the class of quiet degradation AC4 is about.
 *
 * Deduped on both sides for the same reason (see `migrateAnalyticsCardIds`): a view
 * holding a legacy panel AND the variant it maps to would otherwise produce a duplicate.
 *
 * Applied at EVERY read: `applyAnalyticsState` (a view being applied, and the restore on
 * load) and `resetLayout` (the active view being re-read). Seeds come through here too
 * and are unaffected — they already hold current ids, and the map returns anything it
 * has no rule for unchanged.
 */
const migratedState = (state: AnalyticsViewState): AnalyticsViewState => {
  const dimensions = state.filters
  const cards = state.cards ? migrateAnalyticsCardIds(state.cards, dimensions) : state.cards
  if (!state.layouts) return { ...state, cards }

  const layouts: AnalyticsGridLayouts = {}
  for (const [key, items] of Object.entries(state.layouts)) {
    if (!items) continue
    const seen = new Set<string>()
    layouts[key as AnalyticsGridBreakpoint] = items.reduce<AnalyticsGridItem[]>((acc, item) => {
      const i = migrateAnalyticsCardId(item.i, dimensions)
      if (seen.has(i)) return acc
      seen.add(i)
      acc.push({ ...item, i })
      return acc
    }, [])
  }
  return { ...state, cards, layouts }
}

/**
 * Is `id` something the page can actually render, given the enumerated `available` set?
 *
 * A plain lookup for every id in `available` itself. A COMPOSED variant id (VRG-467 —
 * `scans_count+certificates_count::chart`) is never itself a member of `available`
 * (composed ids are user-created by the drag gesture, not part of the fixed metric list
 * `availableCardIds` enumerates — see `buildComposedDisplayVariantCard`'s own header),
 * so without this it would look exactly like an id "that no longer exists" and be
 * dropped by `intersectOrdered` below — on every reload, every view switch and every
 * Cards-panel Reset, since all three route through it. A composed id renders exactly
 * when EVERY one of its series is individually renderable at that display, which is the
 * same test `AnalyticsCanvas.isComposeDropTarget` applies before ever offering the drop
 * — reduces to the plain lookup for a single-metric id, since `parseAnalyticsVariantCardId`
 * returns `null` for anything that isn't a variant at all.
 */
const isRenderableCardId = (id: string, available: ReadonlySet<string>): boolean => {
  if (available.has(id)) return true
  const parsed = parseAnalyticsVariantCardId(id)
  if (!parsed || parsed.metricIds.length < 2) return false
  return parsed.metricIds.every(metricId =>
    // `parsed.grouping` carried through (VERA-260): a composed id keeps its grouping, so
    // testing its series against the PIN id would ask the wrong question.
    available.has(analyticsVariantCardId(metricId, parsed.display, parsed.grouping)),
  )
}

/**
 * Keep only ids the page actually renders, in the view's order.
 *
 * Two failure modes this closes, in opposite directions:
 *  - an id in the view that no longer exists would render a hole, so it is dropped;
 *  - an id the page offers that the view omits is NOT appended back, because omission
 *    is the entire point of Customize. The consequence, stated plainly: a card shipped
 *    after someone saved a view does not appear in that view until they add it. Seeded
 *    views are recomputed from source, so new cards do reach anyone who has not
 *    overridden the default.
 */
const intersectOrdered = (ordered: string[] | undefined, available: string[]): string[] => {
  const have = new Set(available)
  return (ordered ?? available).filter(id => isRenderableCardId(id, have))
}

/** Normalise every breakpoint's array, and drop geometry for cards that are gone. */
const normaliseLayouts = (
  layouts: AnalyticsGridLayouts | undefined,
  cardIds: string[],
): AnalyticsGridLayouts => {
  if (!layouts) return {}
  const have = new Set(cardIds)
  const next: AnalyticsGridLayouts = {}
  for (const [key, items] of Object.entries(layouts)) {
    if (!items) continue
    next[key as AnalyticsGridBreakpoint] = toPersistedItems(items.filter(item => have.has(item.i)))
  }
  return next
}

/** What a removed card needs in order to come back where it was. See `removedPlacement`. */
interface RemovedCardPlacement {
  /** Index in `cardIds` at the moment of removal. Restores a PACKED position. */
  index: number
  /** Explicit geometry per breakpoint, when the card had any. */
  items: Partial<Record<AnalyticsGridBreakpoint, AnalyticsGridItem>>
}

interface UseAnalyticsViewArgs {
  /** Every card id the page can render, in its natural (unordered) order. */
  availableCardIds: string[]
}

export const useAnalyticsView = ({ availableCardIds }: UseAnalyticsViewArgs) => {
  const dispatch = useDispatch()
  const filters = useSelector(state => state.analytics.filters)

  const { activeViewId, savedViews, saveStateOnLogin, isLoading } = useTableViews(ANALYTICS_VIEW_KEY)

  const [cardIds, setCardIds] = useState<string[]>(() => DEFAULT_ANALYTICS_VIEW_STATE.cards ?? [])
  const [layouts, setLayouts] = useState<AnalyticsGridLayouts>(() =>
    normaliseLayouts(DEFAULT_ANALYTICS_VIEW_STATE.layouts, DEFAULT_ANALYTICS_VIEW_STATE.cards ?? []),
  )

  /**
   * Where each card removed in THIS SESSION was sitting — so re-adding puts it back.
   *
   * ## A card's position has TWO possible sources, and both have to be remembered
   *
   * This is the part that is easy to get half-right, because the common case is the one
   * that has no coordinates at all:
   *
   * | source of position | where it lives | what restores it |
   * |---|---|---|
   * | the PACKER (the default) | nowhere — derived from the `cards` ORDER | the card's INDEX |
   * | an explicit arrangement (a drag, or the Logistics seed) | `layouts` | its `AnalyticsGridItem` |
   *
   * Only ONE seed authors `layouts` (see `analyticsViewSeeds`), so on a fresh page
   * `layouts` is `{}` and every card's position is a pure function of the order in
   * `cards` — `resolveLayout` walks `visible` in `cardIds` order and `packCards` fills
   * rows in that order. Remembering geometry alone would therefore have restored nothing
   * in the default view, which is the view almost everyone is in. Re-inserting at the
   * remembered INDEX reproduces the packer's answer exactly, because the packer is
   * deterministic in that order.
   *
   * The index is also what keeps the round trip byte-identical: `addCard` appends, so a
   * remove-then-re-add used to reorder `cards` even when the card visibly landed back in
   * the same place — enough for `ViewsDropdown`'s `stableStringify` comparison to report
   * the view as modified.
   *
   * ## Why the geometry is remembered HERE and not left in `layouts`
   *
   * `removeCard` strips a card's coordinates out of `layouts`, and that has to keep
   * happening. `layouts` is what `currentState` publishes to `ViewsDropdown`, so geometry
   * for a card the view does not show would make two views that look identical fail to
   * compare equal, and the active view would silently de-select itself after a remove.
   *
   * A `ref` sidesteps that completely: it is not state, it is not in `currentState`, and
   * it is therefore invisible to the comparison, to persistence and to every saved view.
   *
   * ## What it does and does not survive
   *
   * Session-scoped by construction: a reload, or applying another view, drops it (the
   * latter explicitly — see `applyAnalyticsState`). That is the honest scope. Persisting
   * a "cards I removed" map into user preferences would be storing the negative space of
   * a view forever, so that a re-add months later lands in a spot the rest of the
   * arrangement has long since moved past.
   *
   * ## Why the coordinates are still valid when the card comes back
   *
   * Removing a card does NOT rewrite anything else's stored geometry. The canvas commits
   * layouts only on `onDragStop` / `onResizeStop`, so the other cards' saved x/y are
   * untouched by a removal — react-grid-layout compacts them upward on screen, but that
   * is a render-time effect of the compactor, not a write.
   */
  const removedPlacement = useRef(new Map<string, RemovedCardPlacement>())

  /** Apply an analytics view payload: card set, geometry, and the dimension filters. */
  const applyAnalyticsState = useCallback(
    (state: AnalyticsViewState | undefined) => {
      // Forward-mapped BEFORE `intersectOrdered`, which is the ordering that matters: an
      // un-migrated legacy id is not in `availableCardIds` any more, so it would be
      // dropped as unknown before the map ever saw it.
      const next = migratedState(state ?? DEFAULT_ANALYTICS_VIEW_STATE)
      const ids = intersectOrdered(next.cards, availableCardIds)
      // A remembered position belongs to the arrangement it was removed from. Carrying
      // it into a different view would drop a card into coordinates nothing else in that
      // view was laid out around.
      removedPlacement.current.clear()
      setCardIds(ids)
      setLayouts(normaliseLayouts(next.layouts, ids))
      // Every key is written, absent reading as "nothing selected" — the same
      // all-keys contract the filter drawer uses, which is what makes both clear
      // paths work without a separate reset callback. `dateRange` is untouched on
      // purpose: the page picker is its sole writer.
      const f = next.filters ?? {}
      dispatch(
        setAnalyticsFilters({
          ownerIds: f.ownerIds ?? [],
          productIds: f.productIds ?? [],
          certificateIds: f.certificateIds ?? [],
          certificateStatuses: f.certificateStatuses ?? [],
          scanStatuses: f.scanStatuses ?? [],
          location: f.location ?? [],
          // Points, from the persisted wire string. `decodeRing` returns null for a
          // malformed or over-cap value, so a view written by another client version
          // restores with no area rather than a partial one — and `?? null` gives the
          // same result for a view saved before this feature existed.
          area: decodeRing(f.area) ?? null,
          // `f.dataType` is the pre-split page-level key. Falling back to it for BOTH
          // panels is what makes an already-saved view keep meaning what it meant: back
          // then one value drove the chart AND the map, so applying it to both is the
          // faithful reading, not a guess. See `AnalyticsDimensionFilters`.
          chartDataType: f.chartDataType ?? f.dataType ?? 'scans',
          mapDataType: f.mapDataType ?? f.dataType ?? 'scans',
        }),
      )
    },
    [availableCardIds, dispatch],
  )

  /** The `applyState` `ViewsDropdown` calls — unwraps the analytics slot. */
  const applyState = useCallback(
    (state: TableViewState | undefined) => applyAnalyticsState(state?.analytics),
    [applyAnalyticsState],
  )

  /** The `onReset` `ViewsDropdown` calls. Back to the default seed, wholesale. */
  const resetToDefault = useCallback(
    () => applyAnalyticsState(DEFAULT_ANALYTICS_VIEW_STATE),
    [applyAnalyticsState],
  )

  /** The active view's own payload, or the default seed when nothing is selected. */
  const activeViewState = useCallback((): AnalyticsViewState => {
    const view =
      ANALYTICS_SEEDED_VIEWS.find(v => v.id === activeViewId) ??
      savedViews.find(v => v.id === activeViewId)
    return view?.state?.analytics ?? DEFAULT_ANALYTICS_VIEW_STATE
  }, [activeViewId, savedViews])

  /**
   * Put the ARRANGEMENT back — the Cards panel's Reset.
   *
   * ⚠️ This and `resetToDefault` are two different promises and must stay that way.
   * `ViewsDropdown`'s footer row is labelled "Reset to default" and its handler calls
   * `setActiveView(null)` BEFORE `onReset`, so it means *abandon the view I am on and
   * go back to Overview*. This one means *put the view I am on back how it shipped*,
   * which is what someone wants after a drag went wrong — clearing their selected view
   * as a side effect of tidying its layout would be a second, unasked-for change. The
   * labels carry the distinction: "Reset to default" against "Reset layout".
   *
   * It restores cards + geometry ONLY. Dimension filters are left alone deliberately:
   * a reader who has narrowed to one product and then made a mess of the layout is
   * asking for the layout back, and wiping their filters would be the same class of
   * unasked-for change. That is also what makes this safe to offer from a panel — the
   * worst it can do is undo dragging.
   */
  const resetLayout = useCallback(() => {
    // Same forward map as `applyAnalyticsState`: the active view may be a user-saved one
    // holding legacy ids, and "put this view back how it was" has to mean the same thing
    // here as it does when the view is applied.
    const next = migratedState(activeViewState())
    const ids = intersectOrdered(next.cards, availableCardIds)
    // Same reason as `applyAnalyticsState`: the arrangement being restored is not the
    // one those coordinates were measured in.
    removedPlacement.current.clear()
    setCardIds(ids)
    setLayouts(normaliseLayouts(next.layouts, ids))
  }, [activeViewState, availableCardIds])

  /** Record one breakpoint's geometry after a drag, resize or drop. */
  const setBreakpointLayout = useCallback(
    (breakpoint: AnalyticsGridBreakpoint, items: AnalyticsGridItem[]) =>
      setLayouts(prev => ({ ...prev, [breakpoint]: toPersistedItems(items) })),
    [],
  )

  /**
   * Add a card — the Layout panel's Add button, and the other half of the per-card `X`.
   *
   * Two paths, decided by whether this card was removed earlier in this session:
   *
   *  - **Never here, or removed in another view** — appended, with no geometry written
   *    at all. That is the point: `resolveLayout` places an unpositioned card below
   *    everything already placed, using the same packer that lays out a seed. Inventing
   *    coordinates here would be a second placement rule to keep in step with that one.
   *  - **Removed from THIS arrangement** — put back at its remembered INDEX, and given
   *    its remembered geometry if it had any. See `removedPlacement` for why the index
   *    is the half that matters in the default view.
   *
   * The second path is what makes the `X` on a card genuinely reversible rather than
   * approximately reversible. Without it, remove-then-re-add silently rearranges the
   * dashboard — which is the one consequence of that control a user could not undo by
   * hand, and therefore the one thing that would have argued for a confirmation dialog.
   * Restoring the position is what removes that argument.
   */
  const addCard = useCallback((id: string) => {
    const remembered = removedPlacement.current.get(id)
    // Consumed on use, so a card added, dragged somewhere new, removed and re-added
    // returns to the NEW spot: the second remove re-records it.
    removedPlacement.current.delete(id)

    setCardIds(prev => {
      if (prev.includes(id)) return prev
      if (!remembered) return [...prev, id]
      // Splice back at the remembered index. Clamped rather than assumed valid: cards
      // may have been removed since, so the index can now be past the end.
      const next = [...prev]
      next.splice(Math.min(remembered.index, next.length), 0, id)
      return next
    })

    if (!remembered || Object.keys(remembered.items).length === 0) return

    setLayouts(prev => {
      const next: AnalyticsGridLayouts = { ...prev }
      for (const [key, item] of Object.entries(remembered.items)) {
        if (!item) continue
        const bp = key as AnalyticsGridBreakpoint
        next[bp] = toPersistedItems([...(prev[bp] ?? []).filter(i => i.i !== id), item])
      }
      return next
    })
  }, [])

  /**
   * Add a card AT a position — the Layout panel's drag onto the canvas.
   *
   * The dropped position WINS over any remembered one, and the memory is discarded: the
   * user has just said where they want it, so restoring a previous spot would be undoing
   * the gesture they made.
   */
  const addCardAt = useCallback(
    (id: string, item: AnalyticsGridItem, breakpoint: AnalyticsGridBreakpoint) => {
      removedPlacement.current.delete(id)
      setCardIds(prev => (prev.includes(id) ? prev : [...prev, id]))
      setLayouts(prev => ({
        ...prev,
        [breakpoint]: toPersistedItems([...(prev[breakpoint] ?? []).filter(i => i.i !== id), item]),
      }))
    },
    [],
  )

  /**
   * Remove a card — the Layout panel's row control AND the `X` on the card itself.
   *
   * Its geometry comes OUT of `layouts` (so a saved view never carries coordinates for a
   * card it does not show — two such views would fail to compare equal and the active
   * view would de-select itself) and goes INTO `removedPlacement`, which is a ref and
   * therefore outside everything that gets compared or persisted. `addCard` reads it
   * back.
   *
   * ⚠️ An earlier version of this comment argued the other way: *"keeping the geometry
   * would look like a kindness … the card comes back packed at the end, which is where
   * the Add button puts every other card anyway."* That reasoning was sound about
   * `layouts` and wrong about the user-visible outcome, and the two are separable. The
   * equality problem is caused by geometry living in the PUBLISHED state; it is not
   * caused by remembering the position at all. Once removal became a control ON the card
   * — one click, on the thing you are looking at — "comes back at the bottom" stopped
   * being an acceptable cost: the natural way to try the page without a card is to
   * remove it and put it back, and that must not quietly rearrange the dashboard.
   */
  const removeCard = useCallback(
    (id: string) => {
      // Read from the committed state rather than inside the updaters: an updater must
      // stay pure, and React may invoke it more than once.
      const index = cardIds.indexOf(id)
      // Not on the canvas — nothing to remove and nothing to remember.
      if (index === -1) return

      const items: Partial<Record<AnalyticsGridBreakpoint, AnalyticsGridItem>> = {}
      for (const [key, bpItems] of Object.entries(layouts)) {
        const own = bpItems?.find(item => item.i === id)
        if (own) items[key as AnalyticsGridBreakpoint] = own
      }
      // The index is recorded even when `items` is empty — that is the packed case, and
      // it is the common one.
      removedPlacement.current.set(id, { index, items })

      setCardIds(prev => prev.filter(x => x !== id))
      setLayouts(prev => {
        const next: AnalyticsGridLayouts = {}
        for (const [key, bpItems] of Object.entries(prev)) {
          if (!bpItems) continue
          next[key as AnalyticsGridBreakpoint] = bpItems.filter(item => item.i !== id)
        }
        return next
      })
    },
    [cardIds, layouts],
  )

  /**
   * ── COMPOSE / DECOMPOSE — VRG-467: rename a card's id EVERYWHERE it is tracked ──────
   *
   * A compose (drag a metric onto an existing chart) or a decompose (remove a series
   * from a composed one) changes a card's IDENTITY without moving it — the card that was
   * `scans_count::chart` is now `scans_count+certificates_count::chart`, sitting in the
   * exact grid cell it already occupied. Both `cardIds` (membership) and every
   * breakpoint's `layouts[bp][].i` (geometry, keyed by id) carry the OLD id, and both
   * have to be rewritten together in one commit — this is the identical pairing
   * `migratedState` already enforces for legacy-id forward-mapping, for the identical
   * reason: renaming only `cardIds` would let the card survive under its new id while
   * `resolveLayout` looks the OLD id up in a layout that no longer has it, silently
   * dropping the card to the bottom of the dashboard. That is a real regression a naive
   * "just update cardIds" implementation would ship, so it is factored into one function
   * both gestures share rather than risking one of them getting only half the fix.
   */
  const renameCardId = useCallback(
    (oldId: string, newId: string) => {
      setCardIds(prev => prev.map(id => (id === oldId ? newId : id)))
      setLayouts(prev => {
        const next: AnalyticsGridLayouts = {}
        for (const [key, items] of Object.entries(prev)) {
          if (!items) continue
          next[key as AnalyticsGridBreakpoint] = toPersistedItems(
            items.map(item => (item.i === oldId ? { ...item, i: newId } : item)),
          )
        }
        return next
      })
      // The renamed card is a live, positioned card, not a removed one — any memory of
      // the OLD id being removed earlier this session no longer applies to it.
      removedPlacement.current.delete(oldId)
      /*
        ⚠️ THE CARD'S BUCKET WIDTH FOLLOWS ITS ID, OR COMPOSING SILENTLY RE-BUCKETS IT.

        Since VRG-512 the "View by" preference is keyed by card id
        (`analyticsCardGranularity`), and this function is where a card's id changes while
        the card itself does not move. Without this line the new id would have no entry, so
        a reader who had deliberately set Day would drag one more series onto the chart and
        watch it re-bucket to Month — no control touched, nothing on screen saying why, and
        the marks still summing to the same totals, so it would look fine while answering a
        different question.

        Here rather than in `composeCard` because all three gestures that rewrite an id —
        compose, decompose, map regroup — funnel through this one function. That is the same
        reason the `cardIds`-plus-geometry pairing above lives here: a rule applied at one
        call site and forgotten at the next two is not a rule.
      */
      renameAnalyticsCardGranularity(oldId, newId)
    },
    [],
  )

  /**
   * The compose gesture (VRG-467): drag a metric onto an existing chart/map card and it
   * joins as another series. A no-op — silently, per `composeAnalyticsVariantCardId`'s
   * own header — when `targetCardId` is not on the canvas, is not a variant, or already
   * carries `metricId` as one of its series (refuse the duplicate).
   */
  const composeCard = useCallback(
    (targetCardId: string, metricId: string) => {
      if (!cardIds.includes(targetCardId)) return
      const nextId = composeAnalyticsVariantCardId(targetCardId, metricId)
      if (!nextId) return
      renameCardId(targetCardId, nextId)
    },
    [cardIds, renameCardId],
  )

  /**
   * The inverse — a per-series remove control on a composed card's legend. A no-op when
   * `cardId` is not on the canvas, `metricId` is not one of its series, or it is the
   * card's ONLY series (see `removeAnalyticsVariantSeries` — that case is "remove the
   * whole card", which is the pre-existing per-card `X`, not this).
   */
  const removeCardSeries = useCallback(
    (cardId: string, metricId: string) => {
      if (!cardIds.includes(cardId)) return
      const nextId = removeAnalyticsVariantSeries(cardId, metricId)
      if (!nextId) return
      renameCardId(cardId, nextId)
    },
    [cardIds, renameCardId],
  )

  /**
   * The map's `Group by:` toggle (VERA-260) — pins or countries.
   *
   * ⚠️ IT IS A RENAME, and that is the whole mechanism rather than an implementation
   * detail. Grouping lives in the card's ID (`…::map@country`), so switching it makes the
   * card a different card sitting in the exact grid cell it already occupied — the SAME
   * shape as compose/decompose above, which is why all three share `renameCardId` and its
   * cardIds-plus-every-breakpoint's-geometry pairing.
   *
   * What that buys, and why the obvious alternative (a `Map<cardId, grouping>` in this
   * hook) was not taken: the grouping is persisted, restored, and carried into a saved
   * view for free, because `cardIds` is already all three of those things. A parallel
   * store would have needed its own slot in `AnalyticsViewState`, its own migration, and
   * its own answer for what happens when a card is removed and re-added.
   *
   * A no-op — silently, per `regroupAnalyticsVariantCardId` — when the card is not on the
   * canvas, is not a map, or is already at that grouping.
   */
  const setCardGrouping = useCallback(
    (cardId: string, grouping: AnalyticsMapGrouping) => {
      if (!cardIds.includes(cardId)) return
      const nextId = regroupAnalyticsVariantCardId(cardId, grouping)
      if (!nextId) return
      renameCardId(cardId, nextId)
    },
    [cardIds, renameCardId],
  )

  /**
   * Re-apply the persisted active view once preferences have loaded.
   *
   * `ViewsDropdown` persists WHICH view is active but only re-applies state on load
   * when "save state on login" is on (it restores `lastViewState` instead). Without
   * this, picking "Marketing" and reloading left the dropdown highlighting Marketing
   * while the page showed the default cards — the highlight would in fact de-select
   * itself on the state mismatch, so the choice was silently lost.
   *
   * Skipped entirely when `saveStateOnLogin` is on, so the two restores never race:
   * that path is `ViewsDropdown`'s and `lastViewState` is the more specific answer.
   */
  const restoredRef = useRef(false)
  useEffect(() => {
    if (restoredRef.current || isLoading) return
    restoredRef.current = true
    if (saveStateOnLogin) return
    const view =
      ANALYTICS_SEEDED_VIEWS.find(v => v.id === activeViewId) ??
      savedViews.find(v => v.id === activeViewId)
    applyAnalyticsState(view?.state?.analytics ?? DEFAULT_ANALYTICS_VIEW_STATE)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, saveStateOnLogin])

  /**
   * The live state, in the envelope `ViewsDropdown` compares and saves.
   *
   * THREE omissions keep a freshly-applied view comparing EQUAL to the seed it came
   * from, rather than de-selecting itself the instant it was applied:
   *  - only non-empty dimension arrays are included, so "nothing selected" and "key
   *    absent" stringify the same;
   *  - a per-panel data type is included only when it is NOT the default `scans`, for
   *    exactly the same reason. This is new, and it FIXES a live mismatch: `dataType`
   *    used to be written unconditionally while the Overview seed declares
   *    `filters: {}`, so the default view published `{dataType:'scans'}` against a seed
   *    holding `{}` and could never compare equal to the thing it had just applied;
   *  - `layouts` is omitted entirely when no breakpoint has been arranged, because a
   *    seed that leaves its geometry to the packer has no `layouts` key either.
   *
   * ⚠️ The deprecated page-level `dataType` is never written here. It is a READ path
   * only (see `applyAnalyticsState`); writing it would put two keys meaning the same
   * thing into every newly-saved view.
   */
  const currentState = useMemo<TableViewState>(() => {
    const dimensions: AnalyticsViewState['filters'] = {}
    if (filters.chartDataType !== 'scans') dimensions.chartDataType = filters.chartDataType
    if (filters.mapDataType !== 'scans') dimensions.mapDataType = filters.mapDataType
    if (filters.ownerIds.length) dimensions.ownerIds = filters.ownerIds
    if (filters.productIds.length) dimensions.productIds = filters.productIds
    if (filters.certificateIds.length) dimensions.certificateIds = filters.certificateIds
    if (filters.certificateStatuses.length) dimensions.certificateStatuses = filters.certificateStatuses
    if (filters.scanStatuses.length) dimensions.scanStatuses = filters.scanStatuses
    if (filters.location.length) dimensions.location = filters.location
    // Encoded, and omitted when there is no area — the same all-or-nothing rule the
    // dimension arrays follow, so a view with no area compares equal to a seed that
    // declares none rather than de-selecting itself the instant it is applied.
    const encodedArea = encodeRing(filters.area)
    if (encodedArea) dimensions.area = encodedArea

    const analytics: AnalyticsViewState = { cards: cardIds, filters: dimensions }
    const arranged = Object.values(layouts).some(items => items && items.length > 0)
    if (arranged) analytics.layouts = layouts

    return { analytics }
  }, [
    cardIds,
    layouts,
    filters.chartDataType,
    filters.mapDataType,
    filters.ownerIds,
    filters.productIds,
    filters.certificateIds,
    filters.certificateStatuses,
    filters.scanStatuses,
    filters.location,
    filters.area,
  ])

  return {
    viewKey: ANALYTICS_VIEW_KEY,
    seededViews: ANALYTICS_SEEDED_VIEWS,
    defaultViewId: DEFAULT_ANALYTICS_VIEW_ID,

    cardIds,
    layouts,
    setBreakpointLayout,
    addCard,
    addCardAt,
    removeCard,
    /** VRG-467 — drag a metric onto an existing chart/map so it joins as another series. */
    composeCard,
    /** VRG-467 — the inverse: drop one metric off a composed card's series list. */
    removeCardSeries,
    /** VERA-260 — the map's `Group by:` toggle. A rename, not a stored preference. */
    setCardGrouping,

    /** Restore the ACTIVE view's cards + geometry. The Cards panel's Reset. */
    resetLayout,

    /** Props to spread onto `ViewsDropdown`. */
    currentState,
    applyState,
    /** "Reset to default" — clears the active view AND restores the Overview seed. */
    resetToDefault,
  }
}
